'use client';

import {
  EventType,
  EventTypeEnum,
  UserEventProgress,
  UserGlobalEventProgress,
  UserGlobalEventResponse,
} from '@wwe-portal/entities/rbe.type';
import { useGetCurrentUserId, useIsAuthenticated } from '../stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getAllRewardsByStatus,
  getScheduledGlobalEvents,
  getGlobalEvents,
  getLeaderboard,
  getRbeLeaderboardByEventId,
  getSoloEvents,
  getUserSoloEvents,
  updateClaimRewardStatuses,
  updateLeaderboardPublicity,
} from '@wwe-portal/web/services/client/rbe';
import { usePathname } from 'next/navigation';
import { useCallback, useMemo } from 'react';
import {
  MEDIUM_CACHE_TIME,
  SHORT_CACHE_TIME,
} from '@wwe-portal/web/utils/config';
import axios from 'axios';
import {
  QUERY_KEY_RBE,
  QUERY_KEY_RBE_GLOBAL_USER_PROGRESS,
  QUERY_KEY_RBE_LEADERBOARD,
  QUERY_KEY_RBE_REWARDS,
  QUERY_KEY_RBE_SOLO_USER_PROGRESS,
} from '@wwe-portal/web/utils/constants/query-key';
import { RbeData } from './rbe.prefetch';
import { useLimitedEventV2 } from '@wwe-portal/web/hooks/rbe/use-limited-events';

const getRefetchInterval = (data: string[] | undefined | null) => {
  if (data == null || data.length === 0) return false;
  const now = Date.now();
  const intervals = data
    .map((date) => Date.parse(date) - now)
    .filter((ms) => ms > 0);
  if (intervals.length === 0) return SHORT_CACHE_TIME;

  const minInterval = Math.min(...intervals, MEDIUM_CACHE_TIME);
  return minInterval + 1000;
};

export const useGetUserSoloEvents = () => {
  const userId = useGetCurrentUserId();
  const { soloEvents, isLoading: isLoadingEventData } = useLimitedEventV2();
  const isAuthenticated = useIsAuthenticated();
  const enabled = isAuthenticated;
  const {
    data,
    isLoading: isLoadingUserData,
    ...rest
  } = useQuery({
    queryKey: [QUERY_KEY_RBE_SOLO_USER_PROGRESS, { userId }],
    queryFn: async () => {
      const response = await getUserSoloEvents();
      return response.data.data;
    },
    enabled,
  });

  const userProgressMap = useMemo(() => {
    const map = new Map<number, UserEventProgress>();
    data?.forEach((item) => {
      map.set(item.portalEventId, item);
    });
    return map;
  }, [data]);

  const isLoading = isLoadingEventData || isLoadingUserData;

  return {
    data,
    soloEvents,
    userProgressMap,
    enabled,
    isLoading,
    ...rest,
  };
};

export const useGetUserGlobalEvents = () => {
  const userId = useGetCurrentUserId();
  const {
    globalEvents,
    isLoading: isLoadingEvents,
    ...rest
  } = useLimitedEventV2();
  const { data: userGlobalEvents, isLoading: isLoadingProgress } = useQuery({
    queryKey: [QUERY_KEY_RBE_GLOBAL_USER_PROGRESS, { userId }],
    queryFn: async () => {
      const response = await getGlobalEvents();
      const userProgress: UserGlobalEventResponse = {
        data: response.data.data.events.map((item) => ({
          portalEventId: item.portalEventId,
          milestones: item.milestones.map((milestone) => ({
            id: milestone.id,
            claimed: false,
            type: milestone.type,
          })),
          totalPoints: item.totalPoints,
          point: item.point,
          participantsCount: item.participantsCount,
        })),
      };
      return userProgress.data;
    },
  });
  const userProgressMap = useMemo(() => {
    const map = new Map<number, UserGlobalEventProgress>();

    globalEvents?.forEach((event) => {
      const userProgress: UserGlobalEventProgress = {
        portalEventId: event.portalEventId,
        milestones: event.milestones.map((milestone) => {
          return {
            id: milestone.id,
            claimed: false,
            type:
              event.totalPoints >= milestone.requirement
                ? 'claim'
                : 'not.ready',
          };
        }),
        userPoint: undefined,
        participantsCount: event.participantsCount,
        point: event.totalPoints,
      };

      map.set(event.portalEventId, userProgress);
    });

    if (userGlobalEvents) {
      userGlobalEvents?.forEach((item) => {
        const currentProgress = map.get(item.portalEventId);
        if (!currentProgress) return;
        currentProgress.milestones = item.milestones;
        currentProgress.userPoint = item.point;
        currentProgress.participantsCount = item.participantsCount;
        map.set(item.portalEventId, currentProgress);
      });
    }

    return map;
  }, [userGlobalEvents, globalEvents]);

  return {
    ...rest,
    isLoading: isLoadingEvents || isLoadingProgress,
    globalEvents,
    userProgressMap,
    userGlobalEvents,
  };
};

export const useInvalidateUserProgress = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_RBE_SOLO_USER_PROGRESS],
    });
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_RBE_GLOBAL_USER_PROGRESS],
    });
  };
};

export const useGetRBE = () => {
  const { data, ...rest } = useQuery({
    queryKey: [QUERY_KEY_RBE],
    queryFn: async () => {
      const [soloRes, globalRes, scheduledEvents] = await Promise.all([
        getSoloEvents(),
        getGlobalEvents(),
        getScheduledGlobalEvents(),
      ]);
      const rbeData: RbeData = {
        soloEvents: soloRes.data.data,
        globalEvents: globalRes.data.data.events,
        globalEventPriority: globalRes.data.data.showFirst ?? 1,
        scheduledEvents: scheduledEvents.data.data,
      };
      return rbeData;
    },
    refetchInterval: (query) => {
      if (query.state.data == null) return getRefetchInterval(undefined);
      const { soloEvents, globalEvents, scheduledEvents } = query.state.data;
      return getRefetchInterval([
        ...(soloEvents?.map((event) => event.endDate) ?? []),
        ...(globalEvents?.map((event) => event.endDate) ?? []),
        ...(scheduledEvents?.map((event) => event.startDate) ?? []),
      ]);
    },
  });
  return {
    data,
    ...rest,
  };
};

export const useInvalidateEvents = () => {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_RBE],
    });
  };
};

export const getLatestInitEvent = async () => {
  const { data } = await getAllRewardsByStatus('CLAIMED');

  if (!data?.data?.length) {
    return null;
  }

  const now = new Date().getTime();
  const events = data?.data?.filter(
    (i) => new Date(i?.endDate).getTime() <= now
  );
  for (const event of events) {
    if (!event.portalEventId) continue;
    try {
      const rbeLeaderboardResponse = await getRbeLeaderboardByEventId(
        event.portalEventId,
        {
          isActive: false,
        }
      );
      return {
        leaderboard: rbeLeaderboardResponse.data,
        eventId: event.portalEventId,
        allEvents: events,
      };
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        continue;
      }
      return null;
    }
  }
  return null;
};

export const useGetRewardStatus = () => {
  const pathname = usePathname();
  const isAuthenticated = useIsAuthenticated();
  return useQuery({
    queryKey: [QUERY_KEY_RBE_REWARDS],
    queryFn: getLatestInitEvent,
    enabled: isAuthenticated,
    retry: false,
    refetchOnMount: true,
    refetchOnWindowFocus: true,

    refetchInterval: (data) => {
      return pathname.includes('limited-time-events') ? 30_000 : 60_000;
    },
  });
};

export const useInvalidateGetRewards = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_RBE_REWARDS],
      }),
    [queryClient]
  );
};

export const useShowRewards = () => {
  return useMutation({
    mutationFn: async (eventIds: string[]) => {
      await Promise.all(eventIds.map(updateClaimRewardStatuses));
    },
  });
};

const isValidEventType = (eventType: number) => {
  return Object.values(EventTypeEnum).includes(eventType as never);
};

const getRbeLeaderboardQueryKeys = ({
  portalEventId,
  userId,
}: {
  portalEventId: string;
  userId: string | undefined;
}) => {
  return [QUERY_KEY_RBE_LEADERBOARD, 'ranks', { portalEventId, userId }];
};

export const useGetLeaderboard = (
  { portalEventId, typeId }: { portalEventId: string; typeId: EventType },
  options?: { enabled?: boolean }
) => {
  const userId = useGetCurrentUserId();
  const { data, isLoading } = useQuery({
    queryKey: getRbeLeaderboardQueryKeys({
      portalEventId,
      userId,
    }),
    queryFn: () =>
      getLeaderboard({
        portalEventId,
        typeId,
        isAuthenticated: userId != null,
      }),
    enabled: options?.enabled && isValidEventType(typeId),
  });

  return {
    leaderboard: data,
    isLoading,
  };
};

export const useInvalidateLeaderboard = () => {
  const queryClient = useQueryClient();
  const invalidateRbe = useInvalidateEvents();
  return async () => {
    await invalidateRbe();

    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_RBE_LEADERBOARD],
    });
  };
};

export const useUpdateUserPublicity = (eventId: string) => {
  const queryClient = useQueryClient();
  const userId = useGetCurrentUserId();
  return useMutation({
    mutationFn: async (
      params: Parameters<typeof updateLeaderboardPublicity>[0]
    ) => {
      queryClient.setQueryData(
        getRbeLeaderboardQueryKeys({
          portalEventId: eventId,
          userId,
        }),
        (data: any) => {
          if (data?.currentPlayer == null) return data;
          return {
            ...data,
            currentPlayer: {
              ...data?.currentPlayer,
              isHidden: params.isAnonymous,
            },
          };
        }
      );
      await updateLeaderboardPublicity(params);
    },
  });
};
